import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { FunnelIcon } from "@heroicons/react/24/solid";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  useFilters,
  useGlobalFilter,
  useTable,
} from "react-table";
import { PageButton } from "../forms/Buttons/PageButton";
import { Button } from "../forms/Buttons/Button";
import { useRowLink } from "../../utils/rowLink";

export default function TableWithServerOrdering({
  columns,
  data,
  count,
  pageNumber,
  nextPage,
  previousPage,
  gotoPage,
  pageCount,
  ordering,
  handleOrdering,
  loading,
  hideCount = false,
  // Narrower cells, for tables with many columns.
  dense = false,
  // Optional: the page a row opens on click (onRowOpen runs first); its own links and buttons keep their clicks.
  getRowHref,
  onRowOpen,
}) {
  const [t] = useTranslation();
  const rowLink = useRowLink();
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns,
        data,
        manualSortBy: true,
        autoResetSortBy: false,
      },
      useFilters,
      useGlobalFilter,
    );

  return (
    <>
      {headerGroups?.map((headerGroup) =>
        headerGroup.headers?.map((column) =>
          column.Filter ? (
            <div key={column.id}>{column.render("Filter")}</div>
          ) : null
        )
      )}
      {!hideCount && (
        <div className="flex items-center justify-between py-3">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button onClick={() => previousPage()} disabled={pageNumber === 1}>
              {t("table.previous")}
            </Button>
            <Button
              onClick={() => nextPage()}
              disabled={pageNumber === pageCount}
            >
              {t("table.next")}
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div className="flex gap-x-2 text-sm text-slate-600">
              {count && (
                <div>
                  <Trans
                    i18nKey="table.total"
                    values={{ count }}
                    components={{ bold: <span className="font-semibold text-slate-900" /> }}
                  />
                </div>
              )}
              {pageNumber && (
                <span>
                  |{" "}
                  <Trans
                    i18nKey="table.page_of"
                    values={{ page: pageNumber, pages: pageCount }}
                    components={{ bold: <span className="font-semibold text-slate-900" /> }}
                  />
                </span>
              )}
            </div>
            {pageCount > 1 && (
              <div>
                <nav
                  className="relative z-0 inline-flex -space-x-px rounded-lg shadow-sm"
                  aria-label={t("table.pagination")}
                >
                  <PageButton
                    className="rounded-l-lg"
                    onClick={() => gotoPage(1)}
                    disabled={pageNumber === 1}
                  >
                    <span className="sr-only">{t("table.first_page")}</span>
                    <ChevronDoubleLeftIcon
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  </PageButton>
                  <PageButton
                    onClick={() => previousPage()}
                    disabled={pageNumber === 1}
                  >
                    <span className="sr-only">{t("table.previous_page")}</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </PageButton>
                  <PageButton
                    onClick={() => nextPage()}
                    disabled={pageNumber === pageCount}
                  >
                    <span className="sr-only">{t("table.next_page")}</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </PageButton>
                  <PageButton
                    className="rounded-r-lg"
                    onClick={() => gotoPage(pageCount)}
                    disabled={pageNumber === pageCount}
                  >
                    <span className="sr-only">{t("table.last_page")}</span>
                    <ChevronDoubleRightIcon
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  </PageButton>
                </nav>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-2 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <table
                {...getTableProps()}
                className="min-w-full divide-y divide-slate-200"
              >
                <thead className="bg-slate-50">
                  {headerGroups?.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers?.map((column) => {
                        return (
                          <th
                            scope="col"
                            className={`group relative select-none ${dense ? "px-4" : "px-6"} py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 ${
                              column.accessor_for_ordering
                                ? "cursor-pointer transition-colors duration-200 hover:text-slate-800"
                                : ""
                            }`}
                            key={column.Header}
                            onClick={() => {
                              if (column.accessor_for_ordering) {
                                handleOrdering(column.accessor_for_ordering);
                              }
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              {column.render("Header")}
                              <span>
                                {column.accessor_for_ordering ? (
                                  ordering ===
                                  `-${column.accessor_for_ordering}` ? (
                                    <ChevronDownIcon className="h-4 w-4 text-blue-600" />
                                  ) : ordering ===
                                    column.accessor_for_ordering ? (
                                    <ChevronUpIcon className="h-4 w-4 text-blue-600" />
                                  ) : (
                                    <FunnelIcon className="h-4 w-4 text-slate-400 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                                  )
                                ) : null}
                              </span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="divide-y divide-slate-100 bg-white"
                >
                  {loading ? (
                    <tr>
                      <td className="px-6 py-10 text-center" colSpan={columns.length}>
                        <div className="text-sm text-slate-500">{t("table.loading")}</div>
                      </td>
                    </tr>
                  ) : (
                    rows?.map((row, i) => {
                      prepareRow(row);
                      const href = getRowHref?.(row.original);
                      return (
                        <tr
                          {...row.getRowProps()}
                          {...(href ? rowLink(href, { onOpen: () => onRowOpen?.(row.original) }) : {})}
                          className={`transition-colors duration-150 hover:bg-slate-50/80 ${href ? "cursor-pointer" : ""}`}
                        >
                          {row.cells?.map((cell) => {
                            return (
                              <td
                                {...cell.getCellProps()}
                                className={`whitespace-nowrap text-sm text-slate-700 ${dense ? "px-4 py-3" : "px-6 py-4"} ${cell.column.cellClassName || ""}`}
                              >
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
